import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, ChevronLeft, ChevronRight, Clock, FileText } from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

function getContractHoursForMonth(contract, yearMonth) {
  if (!contract) return null;
  const m = moment(yearMonth, "YYYY-MM");
  const daysInMonth = m.daysInMonth();
  const weeksInMonth = daysInMonth / 7;
  return Math.round((contract.weekly_hours || 0) * weeksInMonth * 10) / 10;
}

export default function MonthlyHoursManager({ venueId }) {
  const [yearMonth, setYearMonth] = useState(moment().format("YYYY-MM"));
  const [members, setMembers] = useState([]);
  const [contracts, setContracts] = useState({});
  const [planned, setPlanned] = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [actualHours, setActualHours] = useState({});

  useEffect(() => {
    if (venueId) loadAll();
  }, [venueId, yearMonth]);

  const loadAll = async () => {
    setLoading(true);
    const [mems, contractList, plannedList, timeRecords] = await Promise.all([
      hosys.entities.VenueMember.filter({ venue_id: venueId, active: true }),
      hosys.entities.WorkerContract.filter({ venue_id: venueId, active: true }),
      hosys.entities.MonthlyPlannedHours.filter({ venue_id: venueId, year_month: yearMonth }),
      hosys.entities.TimeRecord.filter({ venue_id: venueId }),
    ]);

    // Map contracts by email
    const contractMap = {};
    contractList.forEach(c => { contractMap[c.user_email] = c; });

    // Map planned hours by email
    const plannedMap = {};
    plannedList.forEach(p => { plannedMap[p.user_email] = p; });

    // Calculate actual hours for this month
    const start = yearMonth + "-01";
    const end = moment(yearMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD");
    const monthRecords = timeRecords.filter(r => r.date >= start && r.date <= end && !r.is_deleted);

    const actualMap = {};
    const byWorkerDate = {};
    monthRecords.forEach(r => {
      const key = r.user_email + "_" + r.date;
      if (!byWorkerDate[key]) byWorkerDate[key] = [];
      byWorkerDate[key].push(r);
    });
    Object.entries(byWorkerDate).forEach(([key, recs]) => {
      const email = key.split("_")[0];
      const worked = calcWorkedMinutes(recs);
      actualMap[email] = (actualMap[email] || 0) + worked;
    });

    setMembers(mems.sort((a, b) => (a.user_name || "").localeCompare(b.user_name || "")));
    setContracts(contractMap);
    setPlanned(plannedMap);
    setActualHours(actualMap);

    // Pre-fill editing with existing or contract-based values
    const editMap = {};
    mems.forEach(m => {
      const existing = plannedMap[m.user_email];
      const contractHours = getContractHoursForMonth(contractMap[m.user_email], yearMonth);
      editMap[m.user_email] = existing
        ? String(existing.planned_hours)
        : contractHours != null ? String(contractHours) : "";
    });
    setEditing(editMap);
    setLoading(false);
  };

  const calcWorkedMinutes = (records) => {
    const sorted = [...records].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let total = 0, entryTime = null;
    for (const r of sorted) {
      if (r.type === "entry") entryTime = new Date(r.timestamp);
      else if (r.type === "exit" && entryTime) {
        total += (new Date(r.timestamp) - entryTime) / 60000;
        entryTime = null;
      }
    }
    return Math.max(0, Math.round(total));
  };

  const saveWorker = async (member) => {
    const email = member.user_email;
    const hours = parseFloat(editing[email]);
    if (isNaN(hours) || hours < 0) return;
    setSaving(s => ({ ...s, [email]: true }));

    const contractHours = getContractHoursForMonth(contracts[email], yearMonth);
    const existing = planned[email];
    const data = {
      venue_id: venueId,
      user_email: email,
      user_name: member.user_name,
      year_month: yearMonth,
      planned_hours: hours,
      contract_hours: contractHours,
    };

    if (existing?.id) {
      await hosys.entities.MonthlyPlannedHours.update(existing.id, data);
    } else {
      await hosys.entities.MonthlyPlannedHours.create(data);
    }

    await loadAll();
    setSaving(s => ({ ...s, [email]: false }));
  };

  const saveAll = async () => {
    await Promise.all(members.map(m => saveWorker(m)));
  };

  const prevMonth = () => setYearMonth(moment(yearMonth, "YYYY-MM").subtract(1, "month").format("YYYY-MM"));
  const nextMonth = () => setYearMonth(moment(yearMonth, "YYYY-MM").add(1, "month").format("YYYY-MM"));

  if (loading) return <div className="py-10 text-center text-gray-400 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregant...</div>;

  const totalPlanned = members.reduce((s, m) => s + (parseFloat(editing[m.user_email]) || 0), 0);
  const totalActual = members.reduce((s, m) => s + Math.round((actualHours[m.user_email] || 0) / 60 * 10) / 10, 0);

  return (
    <div className="space-y-5">
      {/* Month selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800">Hores planificades mensuals</h3>
          <p className="text-xs text-gray-400 mt-0.5">Configura les hores a treballar per cada treballador aquest mes</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-gray-800 capitalize min-w-[130px] text-center">
            {moment(yearMonth, "YYYY-MM").format("MMMM YYYY")}
          </span>
          <button
            onClick={nextMonth}
            disabled={yearMonth >= moment().format("YYYY-MM")}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors disabled:opacity-30"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary totals */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
          <p className="text-xs text-blue-500 font-medium">Total hores planificades</p>
          <p className="text-2xl font-bold text-blue-700">{totalPlanned.toFixed(1)}h</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
          <p className="text-xs text-emerald-500 font-medium">Total hores realitzades</p>
          <p className="text-2xl font-bold text-emerald-700">{totalActual.toFixed(1)}h</p>
        </div>
      </div>

      {/* Workers list */}
      <div className="space-y-2">
        {members.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">No hi ha treballadors actius.</div>
        )}
        {members.map(m => {
          const email = m.user_email;
          const contract = contracts[email];
          const contractHours = getContractHoursForMonth(contract, yearMonth);
          const plannedHours = parseFloat(editing[email]) || 0;
          const actualMins = actualHours[email] || 0;
          const actualH = Math.round(actualMins / 60 * 10) / 10;
          const diff = plannedHours > 0 ? (actualH - plannedHours) : null;
          const hasChanges = planned[email]
            ? Math.abs((planned[email].planned_hours || 0) - plannedHours) > 0.01
            : plannedHours > 0;

          return (
            <div key={email} className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#7B2D3F] font-bold text-sm">{(m.user_name || email)[0]?.toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 text-sm truncate">{m.user_name || email}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {contract ? (
                        <span className="text-[11px] text-gray-400">
                          Contracte: {contract.weekly_hours}h/setmana · {contract.workday_type === "part_time" ? "Parcial" : "Complet"}
                        </span>
                      ) : (
                        <span className="text-[11px] text-amber-500 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> Sense contracte configurat
                        </span>
                      )}
                      {contractHours && (
                        <span className="text-[11px] text-gray-400">≈ {contractHours}h/mes</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 items-end">
                <div>
                  <label className="text-[11px] font-medium text-gray-500 mb-1 block">Hores planificades</label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    placeholder={contractHours ? String(contractHours) : "0"}
                    value={editing[email] || ""}
                    onChange={e => setEditing(prev => ({ ...prev, [email]: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="text-center">
                  <label className="text-[11px] font-medium text-gray-500 mb-1 block">Realitzades</label>
                  <div className="h-9 flex items-center justify-center">
                    <span className={`text-sm font-bold ${actualH > 0 ? "text-emerald-600" : "text-gray-300"}`}>
                      {actualH.toFixed(1)}h
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <label className="text-[11px] font-medium text-gray-500 mb-1 block">Diferència</label>
                  <div className="h-9 flex items-center justify-center">
                    {diff !== null ? (
                      <Badge className={`text-xs ${diff >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {diff >= 0 ? "+" : ""}{diff.toFixed(1)}h
                      </Badge>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              {plannedHours > 0 && (
                <div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${actualH >= plannedHours ? "bg-emerald-500" : "bg-[#7B2D3F]"}`}
                      style={{ width: `${Math.min((actualH / plannedHours) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5 text-right">
                    {((actualH / plannedHours) * 100).toFixed(0)}% completat
                  </p>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => saveWorker(m)}
                  disabled={saving[email] || !plannedHours}
                  className={`gap-1.5 text-xs h-8 ${hasChanges ? "bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white" : "bg-gray-100 text-gray-500"}`}
                >
                  {saving[email] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {planned[email] ? "Actualitzar" : "Guardar"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save all */}
      {members.length > 0 && (
        <Button
          onClick={saveAll}
          className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white gap-2"
        >
          <Save className="w-4 h-4" />
          Guardar totes les hores del mes
        </Button>
      )}
    </div>
  );
}